/**
 * FinancialDashboard.js
 * Apple-premium glassmorphism Finance Dashboard.
 *
 * Data source : GET /finance/dashboard?year=YYYY&months=Jan,Feb,...
 * Design      : Matches StudentMaster / ExecutiveDashboard header style.
 *               Uses ThemeContext tokens (light + dark).
 *
 * Charts      : Coaching Performance, Enrollment, Attrition → smooth LINE charts
 *               Income / Expense Trend → bar charts
 */
import React, { useEffect, useMemo, useState, useRef } from "react";
import API_BASE from "./apiConfig";
import { useTheme, getT, glassCard } from "./ThemeContext";
import { useAppConfig } from "./AppContext";
import { 
  FiDollarSign, 
  FiPercent, 
  FiUsers, 
  FiTrendingUp, 
  FiTrendingDown, 
  FiActivity, 
  FiUserPlus, 
  FiUserMinus, 
  FiCheckCircle, 
  FiClock,
  FiBriefcase,
  FiBarChart2,
  FiLayers,
  FiCalendar
} from "react-icons/fi";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtMoney = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};
const fmtPct = (v) => `${Number(v || 0).toFixed(1)}%`;

function fmtMonth(mo) {
  if (!mo) return "";
  const [y, m] = mo.split("-");
  return new Date(+y, +m - 1).toLocaleDateString("en-US", { month: "short" });
}

function scoreColor(pct) {
  return "#f7ce0b"; 
}

// ── ResizeObserver hooks ──────────────────────────────────────────────────────
function useContainerWidth(ref, fallback = 500) {
  const [w, setW] = useState(fallback);
  useEffect(() => {
    if (!ref.current) return;
    setW(ref.current.offsetWidth || fallback);
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref, fallback]);
  return w;
}

function useContainerSize(ref, fw = 500, fh = 200) {
  const [size, setSize] = useState({ w: fw, h: fh });
  useEffect(() => {
    if (!ref.current) return;
    setSize({ w: ref.current.offsetWidth || fw, h: ref.current.offsetHeight || fh });
    const ro = new ResizeObserver(([e]) => setSize({
      w: e.contentRect.width,
      h: Math.max(e.contentRect.height, 60),
    }));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref, fw, fh]); // eslint-disable-line react-hooks/exhaustive-deps
  return size;
}

// ── Spinner ───────────────────────────────────────────────────────────────────
function Spin({ size = 16 }) {
  return (
    <span style={{
      display: "inline-block", width: size, height: size, borderRadius: "50%",
      border: "2px solid rgba(255,255,255,0.15)", borderTopColor: "#f7ce0b",
      animation: "fin-spin 0.7s linear infinite", verticalAlign: "middle",
    }}>
      <style>{`@keyframes fin-spin{to{transform:rotate(360deg)}}`}</style>
    </span>
  );
}

// ── Section label ─────────────────────────────────────────────────────────────
function SectionLabel({ children, T }) {
  return (
    <div style={{ 
      fontSize: 16, fontWeight: 900, color: T.blue, textTransform: "uppercase",
      letterSpacing: "0.15em", marginBottom: 18, marginTop: 14, 
      display: "flex", alignItems: "center", gap: 12 
    }}>
      <div style={{ width: 5, height: 20, borderRadius: 2, background: T.yellow, boxShadow: `0 0 12px ${T.yellow}aa` }}/>
      {children}
    </div>
  );
}

function KpiCard({ icon, label, value, sub, accent = "#60a5fa", T, gc }) {
  return (
    <div style={{
      ...gc, padding: "22px 24px",
      display: "flex", alignItems: "center",
      position: "relative", overflow: "hidden", minHeight: 0,
    }}>
      <div style={{
        width: 66, height: 66, borderRadius: 18, flexShrink: 0,
        background: accent + "16", border: `1.5px solid ${accent}28`,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: `0 6px 18px ${accent}12`,
      }}>
        <div style={{ color: accent, fontSize: 32, display: "flex" }}>{icon}</div>
      </div>
      
      {/* Vertical Gray Line with Gap */}
      <div style={{
        width: 1.5, height: 42,
        background: "rgba(150,150,165,0.22)",
        margin: "0 24px", flexShrink: 0,
      }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 9.5, fontWeight: 800, letterSpacing: "0.12em",
          textTransform: "uppercase", color: T.textMuted, marginBottom: 5,
          lineHeight: 1.3,
        }}>{label}</div>
        <div style={{
          fontSize: 30, lineHeight: 1.05, fontWeight: 900,
          letterSpacing: "-0.04em", color: T.textPrimary,
          fontFamily: "'SF Pro Display','Segoe UI',system-ui,sans-serif",
        }}>{value}</div>
        {sub && <div style={{ fontSize: 10.5, color: T.textMuted, fontWeight: 600, marginTop: 4, display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: accent }} />
          {sub}
        </div>}
      </div>
    </div>
  );
}

function IncomeExpenseCard({ income, expense, T, gc }) {
  const net = income - expense;
  const isP = net >= 0;
  return (
    <div style={{
      ...gc, padding: "22px 24px",
      display: "flex", alignItems: "center",
      position: "relative", overflow: "hidden", minHeight: 0,
    }}>
      <div style={{
        width: 66, height: 66, borderRadius: 18, flexShrink: 0,
        background: "rgba(34,211,238,0.12)", border: "1px solid rgba(34,211,238,0.25)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 6px 18px rgba(34,211,238,0.10)",
      }}>
        <div style={{ color: "#22d3ee", fontSize: 32, display: "flex" }}>
          <FiActivity />
        </div>
      </div>

      {/* Vertical Gray Line with Gap */}
      <div style={{
        width: 1.5, height: 42,
        background: "rgba(150,150,165,0.22)",
        margin: "0 24px", flexShrink: 0,
      }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 9.5, fontWeight: 800, letterSpacing: "0.12em",
          textTransform: "uppercase", color: T.textMuted, marginBottom: 7,
        }}>Income vs Expense</div>
        <div style={{ display: "flex", gap: 16, alignItems: "baseline", marginBottom: 7 }}>
          <div>
            <span style={{ fontSize: 8, fontWeight: 900, textTransform: "uppercase", color: "#34d399", letterSpacing: "0.1em", marginRight: 5 }}>IN</span>
            <span style={{ fontSize: 22, fontWeight: 900, color: "#34d399", letterSpacing: "-0.02em", fontFamily: "'SF Pro Display','Segoe UI',system-ui,sans-serif" }}>{fmtMoney(income)}</span>
          </div>
          <div>
            <span style={{ fontSize: 8, fontWeight: 900, textTransform: "uppercase", color: "#f87171", letterSpacing: "0.1em", marginRight: 5 }}>OUT</span>
            <span style={{ fontSize: 22, fontWeight: 900, color: "#f87171", letterSpacing: "-0.02em", fontFamily: "'SF Pro Display','Segoe UI',system-ui,sans-serif" }}>{fmtMoney(expense)}</span>
          </div>
        </div>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 800,
          background: isP ? "rgba(52,211,153,0.12)" : "rgba(248,113,113,0.12)",
          color: isP ? "#34d399" : "#f87171",
          border: `1px solid ${isP ? "rgba(52,211,153,0.25)" : "rgba(248,113,113,0.25)"}`,
        }}>
          {isP ? <FiTrendingUp size={11} /> : <FiTrendingDown size={11} />}
          Net {isP ? "Profit" : "Loss"}: {fmtMoney(Math.abs(net))}
        </div>
      </div>
    </div>
  );
}



// ══════════════════════════════════════════════════════════════════════════════
//  LINE CHART — smooth Catmull-Rom with filled area, dots and value labels
// ══════════════════════════════════════════════════════════════════════════════

function catmullRomBezier(pts, tension = 0.3) {
  if (pts.length < 2) return "";
  if (pts.length === 2) return `M${pts[0][0]},${pts[0][1]}L${pts[1][0]},${pts[1][1]}`;
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i === 0 ? 0 : i - 1];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2 < pts.length ? i + 2 : i + 1];
    const cp1x = p1[0] + (p2[0] - p0[0]) * tension;
    const cp1y = p1[1] + (p2[1] - p0[1]) * tension;
    const cp2x = p2[0] - (p3[0] - p1[0]) * tension;
    const cp2y = p2[1] - (p3[1] - p1[1]) * tension;
    d += `C${cp1x},${cp1y},${cp2x},${cp2y},${p2[0]},${p2[1]}`;
  }
  return d;
}

function LineChart({ data, lines, T, showDots = true }) {
  const ref = useRef(null);
  const w   = useContainerWidth(ref, 440);

  const empty = !data || data.length === 0 || lines.every(l => data.every(d => !d[l.key]));
  if (empty) {
    return (
      <div ref={ref} style={{ flex: 1, minHeight: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: T.gray, fontSize: 12 }}>No data in this period</span>
      </div>
    );
  }

  const topPad  = 26;
  const btmPad  = 24;
  const chartH  = 130;
  const totalH  = topPad + chartH + btmPad;
  const leftPad = 28;
  const rightPad = 12;
  const n = data.length;

  const allVals = data.flatMap(d => lines.map(l => Number(d[l.key] || 0)));
  const maxVal  = Math.max(...allVals, 1);
  const stepX   = n > 1 ? (w - leftPad - rightPad) / (n - 1) : 0;

  const getX = (i) => leftPad + i * stepX;
  const getY = (v) => topPad + chartH - (Math.min(v, maxVal) / maxVal) * chartH;

  return (
    <div ref={ref} style={{ flex: 1, minHeight: 80, overflow: "hidden" }}>
      {/* Legend */}
      <div style={{ display: "flex", gap: 16, marginBottom: 6, flexWrap: "wrap" }}>
        {lines.map(l => (
          <div key={l.key} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: T.gray }}>
            <div style={{ width: 14, height: 3, borderRadius: 2, background: l.color }} />
            {l.label}
          </div>
        ))}
      </div>

      <svg width={w} height={totalH} style={{ fontFamily: T.font, display: "block" }}>
        {/* Grid */}
        {[0, 25, 50, 75, 100].map(pct => {
          const y = topPad + chartH - (pct / 100) * chartH;
          const val = Math.round(maxVal * pct / 100);
          return (
            <g key={pct}>
              <line x1={leftPad} y1={y} x2={w - rightPad} y2={y}
                stroke={T.divider} strokeWidth={1} strokeDasharray={pct > 0 && pct < 100 ? "3,3" : ""} />
              {pct > 0 && val > 0 && (
                <text x={leftPad - 4} y={y + 3} fontSize={8} fill={T.gray} textAnchor="end">{val}</text>
              )}
            </g>
          );
        })}

        {/* Lines + areas */}
        {lines.map(l => {
          const pts = data.map((d, i) => [getX(i), getY(Number(d[l.key] || 0))]);
          const lineD = catmullRomBezier(pts);
          // Filled area
          const baseY = topPad + chartH;
          const areaD = lineD
            + `L${pts[pts.length - 1][0]},${baseY}`
            + `L${pts[0][0]},${baseY}Z`;

          return (
            <g key={l.key}>
              {/* Area fill */}
              <path d={areaD} fill={l.color} fillOpacity={0.08} />
              {/* Smooth line */}
              <path d={lineD} fill="none" stroke={l.color} strokeWidth={2.5}
                strokeLinecap="round" strokeLinejoin="round" />
              {/* Dots + value labels */}
              {showDots && pts.map((p, i) => {
                const val = Number(data[i][l.key] || 0);
                if (val === 0 && lines.length > 1) return null;
                return (
                  <g key={i}>
                    <circle cx={p[0]} cy={p[1]} r={3.5} fill={l.color} stroke={T.isDark ? "#0a1628" : "#fff"}
                      strokeWidth={2} />
                    {/* Show value on every other point to avoid clutter, or all if few points */}
                    {(n <= 6 || i % 2 === 0) && val > 0 && (
                      <text x={p[0]} y={p[1] - 9} textAnchor="middle" fontSize={9}
                        fill={l.color} fontWeight="700">{val}</text>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Month labels */}
        {data.map((d, i) => (
          <text key={i} x={getX(i)} y={topPad + chartH + 16}
            textAnchor="middle" fontSize={10} fill={T.gray} fontWeight="600">
            {fmtMonth(d.month)}
          </text>
        ))}
      </svg>
    </div>
  );
}

// ── Performance line chart (score-coloured line) ─────────────────────────────
function PerfLineChart({ data, T }) {
  const ref = useRef(null);
  const w   = useContainerWidth(ref, 500);

  const valid = (data || []).filter(d => d.avg_pct != null);
  if (valid.length === 0) {
    return (
      <div ref={ref} style={{ flex: 1, minHeight: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: T.gray, fontSize: 12 }}>No performance data in this period</span>
      </div>
    );
  }

  const topPad  = 26;
  const btmPad  = 24;
  const chartH  = 130;
  const totalH  = topPad + chartH + btmPad;
  const leftPad = 34;
  const rightPad = 12;
  const n = valid.length;
  const stepX = n > 1 ? (w - leftPad - rightPad) / (n - 1) : 0;

  const getX = (i) => leftPad + i * stepX;
  const getY = (pct) => topPad + chartH - (Math.min(pct, 100) / 100) * chartH;

  const pts = valid.map((d, i) => [getX(i), getY(d.avg_pct)]);
  const lineD = catmullRomBezier(pts);
  const baseY = topPad + chartH;
  const areaD = lineD + `L${pts[pts.length - 1][0]},${baseY}L${pts[0][0]},${baseY}Z`;

  // Use gradient for the performance line
  const gradId = "perf-grad-" + Math.random().toString(36).slice(2, 8);

  return (
    <div ref={ref} style={{ flex: 1, minHeight: 80, overflow: "hidden" }}>
      {/* Legend */}
      <div style={{ display: "flex", gap: 18, marginBottom: 6, flexWrap: "wrap" }}>
        {[["≥ 75%", "#34d399"], ["50–74%", "#fbbf24"], ["< 50%", "#f87171"]].map(([l, c]) => (
          <div key={l} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: T.gray }}>
            <div style={{ width: 9, height: 9, borderRadius: 2, background: c }} />{l}
          </div>
        ))}
      </div>

      <svg width={w} height={totalH} style={{ fontFamily: T.font, display: "block" }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#34d399" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#34d399" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Grid */}
        {[25, 50, 75, 100].map(pct => {
          const y = topPad + chartH - (pct / 100) * chartH;
          return (
            <g key={pct}>
              <text x={leftPad - 4} y={y + 3} fontSize={8} fill={T.gray} textAnchor="end">{pct}%</text>
              <line x1={leftPad} y1={y} x2={w - rightPad} y2={y}
                stroke={T.divider} strokeWidth={1} strokeDasharray="3,3" />
            </g>
          );
        })}

        {/* Area fill */}
        <path d={areaD} fill={`url(#${gradId})`} />

        {/* Smooth line */}
        <path d={lineD} fill="none" stroke="#34d399" strokeWidth={2.5}
          strokeLinecap="round" strokeLinejoin="round" />

        {/* Dots + labels */}
        {valid.map((d, i) => {
          const color = scoreColor(d.avg_pct);
          return (
            <g key={i}>
              <circle cx={pts[i][0]} cy={pts[i][1]} r={4} fill={color}
                stroke={T.isDark ? "#0a1628" : "#fff"} strokeWidth={2} />
              <text x={pts[i][0]} y={pts[i][1] - 10} textAnchor="middle" fontSize={10}
                fill={color} fontWeight="800">{d.avg_pct}%</text>
              <text x={pts[i][0]} y={topPad + chartH + 16}
                textAnchor="middle" fontSize={10} fill={T.gray} fontWeight="600">
                {fmtMonth(d.month)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Simple bar chart (income or expense) ─────────────────────────────────────
function SimpleBarChart({ rows, valueKey, color, T }) {
  const ref = useRef(null);
  const { w: cW, h: cH } = useContainerSize(ref, 400, 170);

  if (!rows || rows.length === 0) {
    return (
      <div ref={ref} style={{ flex: 1, minHeight: 70, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: T.gray, fontSize: 12 }}>No data</span>
      </div>
    );
  }

  const topPad = 22; const labelH = 20;
  const chartH = Math.max(cH - topPad - labelH - 2, 40);
  const totalH = topPad + chartH + labelH;
  const leftPad = 8;
  const n = rows.length;
  const slot = Math.max(20, Math.floor((cW - leftPad - 4) / n));
  const barW = Math.max(10, Math.floor(slot * 0.58));
  const gap  = slot - barW;
  const maxVal = Math.max(...rows.map(r => Number(r[valueKey] || 0)), 1);

  return (
    <div ref={ref} style={{ flex: 1, minHeight: 70, overflow: "hidden" }}>
      <svg width={cW} height={totalH} style={{ fontFamily: T.font, display: "block" }}>
        {[25, 50, 75, 100].map(pct => {
          const y = topPad + chartH - (pct / 100) * chartH;
          return <line key={pct} x1={leftPad} y1={y} x2={cW} y2={y}
            stroke={T.divider} strokeWidth={1} strokeDasharray="3,3" />;
        })}
        {rows.map((r, i) => {
          const val = Number(r[valueKey] || 0);
          const bh  = val > 0 ? Math.max((val / maxVal) * chartH, 4) : 0;
          const bx  = leftPad + gap / 2 + i * slot;
          const by  = topPad + chartH - bh;
          return (
            <g key={r.month || i}>
              {val > 0 && (
                <>
                  <rect x={bx} y={by} width={barW} height={bh}
                    fill={color} fillOpacity={0.82} rx={4} />
                  <text x={bx + barW / 2} y={Math.max(by - 3, topPad + 8)}
                    textAnchor="middle" fontSize={8} fill={color} fontWeight="bold">
                    {fmtMoney(val)}
                  </text>
                </>
              )}
              <text x={bx + barW / 2} y={topPad + chartH + 14}
                textAnchor="middle" fontSize={9} fill={T.gray} fontWeight="600">
                {r.month}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Chart wrapper card ────────────────────────────────────────────────────────
function ChartCard({ title, icon, minH = 190, T, gc, children }) {
  return (
    <div style={{ ...gc, padding: "14px 18px", minHeight: minH, display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexShrink: 0 }}>
        <span style={{ color: T.yellow, display: "inline-flex" }}>{icon}</span>
        <span style={{
          fontSize: 11, fontWeight: 800, letterSpacing: "0.06em",
          textTransform: "uppercase", color: T.textPrimary,
        }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

// Removed old static icons - now using react-icons/fi


// ══════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════

export default function FinancialDashboard({
  selectedYear   = String(new Date().getFullYear()),
  selectedMonths = MONTHS,
  hideHeader     = false,
}) {
  const { isDark } = useTheme();
  const T          = getT(isDark);
  const gc         = glassCard(T);
  const { config } = useAppConfig();
  const orgName    = config?.org_name || "Sixers Cricket Academy";

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  // ── Fetch (safe — reads text first, then parses JSON) ─────────────────────
  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const mths = (selectedMonths?.length ? selectedMonths : MONTHS).join(",");
        const url  = `${API_BASE}/finance/dashboard?year=${selectedYear}&months=${encodeURIComponent(mths)}`;
        const res  = await fetch(url);
        const text = await res.text();

        if (text.trimStart().startsWith("<")) {
          throw new Error(
            "Backend returned HTML instead of JSON — the /finance/dashboard route " +
            "is not registered. Check the server startup log for '✗ FAILED finance' " +
            "and restart the server after fixing finance.py."
          );
        }

        let json;
        try { json = JSON.parse(text); }
        catch { throw new Error("Server response is not valid JSON: " + text.slice(0, 120)); }

        if (!res.ok) throw new Error(json.detail || "Failed to load finance data");
        if (alive) setData(json);
      } catch (e) {
        if (alive) setError(String(e.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [selectedYear, selectedMonths]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const summary   = data?.summary   || {};
  const monthly   = data?.monthly   || [];
  const enroll    = data?.enrollment_trend  || [];
  const perfTrend = data?.performance_trend || [];

  const monthCount = selectedMonths?.length || 12;
  const isProfit   = (summary.net_profit_loss || 0) >= 0;

  const headerGrad = isDark
    ? "linear-gradient(118deg,#02304f 0%,#034e7b 60%,#0369a1 100%)"
    : "linear-gradient(118deg,#034e7b 0%,#0369a1 60%,#0284c7 100%)";

  return (
    <div style={{ minHeight: "100%", background: T.pageBg, fontFamily: T.font }}>

      {/* ══ HEADER ════════════════════════════════════════════════════════════ */}
      {!hideHeader && (
        <div style={{
          background: headerGrad,
          boxShadow: "0 4px 20px rgba(3,78,123,0.28)",
          padding: "14px 24px 13px",
          display: "flex", alignItems: "center",
          justifyContent: "space-between", flexWrap: "wrap", gap: 10,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: "rgba(255,255,255,0.14)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)", flexShrink: 0,
            }}>
              <div style={{ fontSize: 24, color: "#fff", display: "flex" }}>
                <FiDollarSign />
              </div>
            </div>
            <div>
              <div style={{
                fontSize: 9, fontWeight: 800, color: "rgba(247,206,11,0.9)",
                textTransform: "uppercase", letterSpacing: "0.12em",
              }}>{orgName}</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: "#fff", lineHeight: 1.2 }}>
                Finance Dashboard
              </div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.50)", marginTop: 1 }}>
                Financial performance · Equity excluded
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              padding: "5px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700,
              background: "rgba(247,206,11,0.18)", border: "1px solid rgba(247,206,11,0.40)",
              color: "#f7ce0b", display: "flex", alignItems: "center", gap: 6,
            }}>
              <FiCalendar size={13} strokeWidth={2.5} />
              {monthCount} month{monthCount !== 1 ? "s" : ""} · {selectedYear}
            </div>
            {loading && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, color: "rgba(255,255,255,0.6)", fontSize: 12 }}>
                <Spin /> Loading…
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ CONTENT ═══════════════════════════════════════════════════════════ */}
      <div style={{ padding: "16px 24px 40px" }}>

        {error && (
          <div style={{
            ...gc, padding: "10px 14px", marginBottom: 14,
            color: "#f87171", fontWeight: 700, fontSize: 12,
            background: "rgba(248,113,113,0.10)",
            border: "1px solid rgba(248,113,113,0.25)",
          }}>{error}</div>
        )}

        {/* ── KPI Row — compact, minimal padding ── */}
        <SectionLabel T={T}>Key Metrics — {selectedYear}</SectionLabel>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 10, marginBottom: 16,
        }}>
          <KpiCard
            icon={<FiDollarSign />}
            label="Net Profit / Loss"
            value={fmtMoney(summary.net_profit_loss)}
            sub={isProfit ? "Profit" : "Loss"}
            accent={isProfit ? "#34d399" : "#f87171"}
            T={T} gc={gc}
          />
          <KpiCard
            icon={<FiPercent />}
            label="Profit Margin"
            value={fmtPct(summary.profit_margin_pct)}
            sub="Excl. Equity"
            accent="#a78bfa"
            T={T} gc={gc}
          />
          <KpiCard
            icon={<FiUsers />}
            label="Total Students"
            value={String(summary.total_active_students ?? 0)}
            sub="Active"
            accent="#60a5fa"
            T={T} gc={gc}
          />
          <IncomeExpenseCard
            income={summary.total_income || 0}
            expense={summary.total_expense || 0}
            T={T} gc={gc}
          />
        </div>

        {/* ── Financial Trends (bar charts) ── */}
        <SectionLabel T={T}>Financial Trends</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <ChartCard title="Income Trend" icon={<FiBarChart2 />} T={T} gc={gc}>
            {loading
              ? <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin size={20} /></div>
              : <SimpleBarChart rows={monthly} valueKey="income" color="#34d399" T={T} />
            }
          </ChartCard>
          <ChartCard title="Expense Trend" icon={<FiBarChart2 />} T={T} gc={gc}>
            {loading
              ? <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin size={20} /></div>
              : <SimpleBarChart rows={monthly} valueKey="expense" color="#f87171" T={T} />
            }
          </ChartCard>
        </div>

        {/* ── Enrollment & Attrition (LINE charts) ── */}
        <SectionLabel T={T}>Enrollment Movement</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <ChartCard title="Enrollment Trend" icon={<FiUserPlus />} T={T} gc={gc}>
            {loading
              ? <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin size={20} /></div>
              : <LineChart data={enroll} T={T}
                  lines={[{ key: "joined", color: "#34d399", label: "Joined" }]} />
            }
          </ChartCard>
          <ChartCard title="Attrition Trend" icon={<FiUserMinus />} T={T} gc={gc}>
            {loading
              ? <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin size={20} /></div>
              : <LineChart data={enroll} T={T}
                  lines={[
                    { key: "suspended", color: "#fbbf24", label: "Suspended" },
                    { key: "cancelled", color: "#f87171", label: "Cancelled" },
                  ]} />
            }
          </ChartCard>
        </div>

        {/* ── Coaching Performance Trend (LINE chart) ── */}
        <SectionLabel T={T}>Coaching Performance</SectionLabel>
        <ChartCard title="Monthly Avg Score %" icon={<FiTrendingUp />} T={T} gc={gc} minH={210}>
          {loading
            ? <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin size={20} /></div>
            : <PerfLineChart data={perfTrend} T={T} />
          }
        </ChartCard>

      </div>
    </div>
  );
}